import React, {useState} from 'react';
import {View} from 'react-native';
import isFinite from 'lodash/isFinite';
import PropTypes from 'prop-types';

import {
  Dialog,
  FormTextInput,
  FormNumberInput,
  Checkbox,
  Text,
  Pressable,
  RadioButton,
} from '~/components/controls';
import {dealer as dealerApi} from '~/api/private';
import {SOURCES} from '~/types/sources';

import sy from '~/styles';

import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';

const DEALER_PART_TYPE = {
  vehicle: 'vehicle',
  all: 'all',
};

const EMPTY_ARTICLE = {
  description: '',
  number: '',
  price: null,
  is_dealer_part: false,
  dealer_part_type: DEALER_PART_TYPE.vehicle,
};

const AddPartDialog = ({
  dealer_id,
  license,
  edit_article,
  item_mp_id,
  onDismiss,
  onAdd,
  onEdited,
  onDeleted,
}) => {
  const [article, setArticle] = useState(edit_article ?? EMPTY_ARTICLE);

  return (
    <Dialog
      visible={true}
      title={
        edit_article ? (
          <View
            style={[
              sy['flex-row'],
              sy['justify-between'],
              sy['items-center'],
              {
                display: 'flex',
              },
            ]}>
            <Text style={[sy.large, {fontSize: 20, lineHeight: 30}]}>
              Artikel bewerken
            </Text>
          </View>
        ) : (
          'Artikel invoeren'
        )
      }
      titleIcon={
        edit_article ? (
          <Pressable
            onPress={async () => {
              let success = true;

              const {reference} = edit_article;
              if (reference) {
                ({success} = await dealerApi.delete_part({
                  id: reference,
                }));
              }

              if (success) {
                onDeleted();
              }
            }}>
            <DeleteIcon fill="#4A4A49" />
          </Pressable>
        ) : null
      }
      onDismiss={onDismiss}
      buttons={[
        {text: 'Annuleren', onPress: onDismiss},
        {
          text: 'OK',
          onPress: async () => {
            if (!article.description || !isFinite(article.price)) {
              return;
            }

            let success = true;
            let part_id = null;

            if (edit_article) {
              const {reference} = edit_article;
              if (reference) {
                ({success} = await dealerApi.update_part({
                  ...article,
                  id: reference,
                }));
              }

              if (success) {
                onEdited({
                  ...article,
                });
              }
            } else {
              let reference = null;
              if (article?.is_dealer_part) {
                ({success, part_id} = await dealerApi.add_part({
                  ...article,
                  item_mp_id,
                  dealer_id,
                  license,
                }));

                reference = part_id;
              }

              if (success) {
                onAdd({
                  ...article,
                  reference,
                  source: SOURCES.Custom,
                });
              }
            }
          },
        },
      ]}>
      <View style={[{paddingTop: 24}, sy['pb-4'], sy['gap-4']]}>
        <FormTextInput
          label="Naam"
          value={article.description}
          onChangeText={(value) =>
            setArticle({
              ...article,
              description: value,
            })
          }
        />
        <FormTextInput
          label="Nummer"
          value={article.number}
          onChangeText={(value) =>
            setArticle({
              ...article,
              number: value,
            })
          }
        />
        <FormNumberInput
          label="Brutoprijs"
          value={article.price}
          digits={2}
          onChangeNumber={(value) =>
            setArticle({
              ...article,
              price: value,
            })
          }
        />
        {!edit_article && /^\d+$/g.test(`${item_mp_id}`) && (
          <View>
            <Pressable
              style={[sy['flex-row'], sy['items-center']]}
              onPress={() => {
                setArticle({
                  ...article,
                  is_dealer_part: !article.is_dealer_part,
                  dealer_part_type: DEALER_PART_TYPE.vehicle,
                });
              }}>
              <Checkbox checked={article.is_dealer_part} />
              <Text style={[sy['pl-4']]}>Opslaan voor hergebruik</Text>
            </Pressable>
            {article?.is_dealer_part && (
              <View style={[sy['px-4'], sy['pt-4'], sy['gap-4']]}>
                <Pressable
                  style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}
                  onPress={() => {
                    setArticle({
                      ...article,
                      dealer_part_type: DEALER_PART_TYPE.vehicle,
                    });
                  }}>
                  <RadioButton
                    checked={
                      article.dealer_part_type === DEALER_PART_TYPE.vehicle
                    }
                  />
                  <Text>Bij dit type voertuig</Text>
                </Pressable>
                <Pressable
                  style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}
                  onPress={() => {
                    setArticle({
                      ...article,
                      dealer_part_type: DEALER_PART_TYPE.all,
                    });
                  }}>
                  <RadioButton
                    checked={article.dealer_part_type === DEALER_PART_TYPE.all}
                  />
                  <Text>Bij alle voertuigen</Text>
                </Pressable>
              </View>
            )}
          </View>
        )}
      </View>
    </Dialog>
  );
};

AddPartDialog.propTypes = {
  dealer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  license: PropTypes.string,
  edit_article: PropTypes.shape({
    reference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  item_mp_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDismiss: PropTypes.func,
  onAdd: PropTypes.func,
  onEdited: PropTypes.func,
  onDeleted: PropTypes.func,
};

export default AddPartDialog;
